import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDown, CheckCircle2, Zap, HeartPulse, Brain, Sparkles, Wind } from "lucide-react";
import { api } from "@/lib/api";
import { useReveal } from "@/hooks/useReveal";
import AnimatedBackdrop from "@/components/AnimatedBackdrop";
import SceneBackdrop from "@/components/SceneBackdrop";
import CertificationsStrip from "@/components/CertificationsStrip";
import TestimonialsWall from "@/components/TestimonialsWall";
import ChamberCompare from "@/components/ChamberCompare";
import ApproveButton from "@/components/ApproveButton";
import ClinicRenderStudio from "@/components/ClinicRenderStudio";

const VIDEO_SRC =
  "https://customer-assets-lqy194kg.emergentagent.net/job_luxe-med-spa/artifacts/dp6sg6xa_agent_generate_video%2520-%2520SCENE%25201_Image%25201%2520depicts%2520the%2520silver%2520recovery%2520pod_s%2520exterior__.mp4";

const HYPERBARIC_SLUGS = ["hyperbaric-oxygen-chamber", "space-capsule-hyperbaric-chamber", "theratec-2-seater"];

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export default function Hyperbaric() {
  const [products, setProducts] = useState([]);
  const [chambersRef, chambersRevealed] = useReveal();
  const [scienceRef, scienceRevealed] = useReveal();

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      const list = data.products || data;
      setProducts(list.filter((p) => HYPERBARIC_SLUGS.includes(p.slug)));
    });
  }, []);

  return (
    <div data-testid="hyperbaric-page" className="bg-[#FAF9F6] text-[#1C1C1C]">
      {/* HERO — full-screen video (stays dark for text legibility over video) */}
      <section className="relative h-screen w-full overflow-hidden text-[#FAF9F6]">
        <video
          data-testid="hyperbaric-hero-video"
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="https://customer-assets.emergentagent.com/job_luxe-med-spa/artifacts/bxsxzaem_IMG_0926.png"
        >
          <source src={VIDEO_SRC} type="video/mp4" />
        </video>
        {/* Dark overlay for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/40 via-[#0A0A0A]/30 to-[#0A0A0A]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_20%,_rgba(10,10,10,0.6)_100%)]" />

        {/* Hero content */}
        <div className="relative z-10 h-full flex flex-col justify-between max-w-[1440px] mx-auto px-6 lg:px-12 py-24">
          <div className="pt-16 lg:pt-24">
            <div
              className="overline text-[#C9A961]"
              style={{ animation: "tt-fade-up 700ms ease both" }}
            >
              TheraTec · Hyperbaric Therapy
            </div>
          </div>

          <div className="max-w-4xl" style={{ animation: "tt-fade-up 900ms ease 200ms both" }}>
            <h1 className="font-serif-editorial text-5xl md:text-7xl lg:text-8xl leading-[1.02] tracking-tight">
              The clinical <em className="italic text-[#C9A961]">oxygen suite</em><br />
              behind the world's<br />
              most discerning practices.
            </h1>
            <p className="mt-8 text-base md:text-lg text-[#D8D3C7] max-w-xl leading-relaxed">
              Two premium chambers. One installed revenue line. Engineered for single-person clinical hyperbaric oxygen therapy — with concierge white-glove delivery and staff certification.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#chambers"
                data-testid="hyperbaric-hero-cta"
                className="inline-flex items-center gap-3 bg-[#C9A961] text-[#0A0A0A] px-8 py-4 font-medium tracking-wide hover:bg-[#FAF9F6] transition-colors duration-300"
              >
                Explore the chambers
                <ArrowDown size={16} />
              </a>
              <a
                href="#visualize"
                data-testid="hyperbaric-visualize-cta"
                className="inline-flex items-center gap-3 border border-[#C9A961]/60 text-[#C9A961] px-8 py-4 font-medium tracking-wide hover:bg-[#C9A961] hover:text-[#0A0A0A] transition-colors duration-300"
              >
                Request a free render
                <Sparkles size={16} />
              </a>
              <Link
                to="/integration"
                className="inline-flex items-center gap-3 border border-[#FAF9F6]/40 text-[#FAF9F6] px-8 py-4 font-medium tracking-wide hover:bg-[#FAF9F6] hover:text-[#0A0A0A] transition-colors duration-300"
              >
                White-glove install
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>

          <div className="flex items-end justify-between text-xs text-[#D8D3C7]/60">
            <div className="overline">Certified · 2.0 ATA · Med-Spa Ready</div>
            <div className="hidden md:flex items-center gap-3">
              <span className="h-px w-8 bg-[#C9A961]" />
              <span>Scroll to enter</span>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO — cinematic pull-quote */}
      <section className="relative max-w-[1200px] mx-auto px-6 lg:px-12 py-24 lg:py-32 overflow-hidden">
        <SceneBackdrop variant="cream" label="RESTORE" art="molecular" />
        <div className="relative z-10 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-3">
            <div className="overline text-[#C9A961]">Why Pressure</div>
          </div>
          <div className="lg:col-span-9">
            <h2 className="font-serif-editorial text-3xl md:text-4xl lg:text-5xl leading-tight text-[#1C1C1C]">
              Hyperbaric Oxygen Therapy delivers <em className="italic tt-shimmer-gold">100% oxygen at elevated pressure</em> — dissolving oxygen directly into plasma, saturating tissues, and accelerating recovery your treatments alone can't reach.
            </h2>
            <div className="mt-10 grid sm:grid-cols-3 gap-6 text-sm text-[#5C5C5C]">
              <Metric value="2.0 ATA" label="Peak pressure" />
              <Metric value="60 min" label="Standard session" />
              <Metric value="1200%" label="Oxygen saturation gain" />
            </div>
          </div>
        </div>
      </section>

      {/* THE CHAMBERS */}
      <TestimonialsWall
        eyebrow="Chosen By The Trade"
        headline="Med-spas across the country "
        headlineItalic="already run TheraTec."
      />
      <section
        id="chambers"
        ref={chambersRef}
        className={`relative max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-32 tt-reveal ${chambersRevealed ? "is-revealed" : ""}`}
      >
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div className="max-w-xl">
            <div className="overline text-[#C9A961]">The Chambers</div>
            <h2 className="font-serif-editorial text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight">
              Three chambers.<br />
              <em className="italic text-[#C9A961]">Each flagship.</em>
            </h2>
          </div>
          <div className="max-w-sm text-sm text-[#5C5C5C] flex flex-col gap-4">
            <p>
              Each unit ships with concierge crating, certified on-site installation, staff certification, and a launch-week protocol library.
            </p>
            <a
              href="#compare"
              data-testid="jump-to-compare"
              className="group inline-flex items-center gap-2 overline text-[#C9A961] hover:gap-3 transition-all duration-300 self-start"
            >
              <span className="h-px w-6 bg-[#C9A961]" />
              Compare all three side-by-side
              <ArrowDown size={14} className="group-hover:translate-y-1 transition-transform duration-500" />
            </a>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {products.map((p, i) => (
            <div
              key={p.slug}
              className="flex flex-col"
              style={{
                animation: chambersRevealed ? `tt-fade-up 700ms ease ${i * 200}ms both` : "none",
              }}
            >
              <Link
                to={`/product/${p.slug}`}
                data-testid={`hyperbaric-chamber-${p.slug}`}
                className="group relative overflow-hidden bg-[#FAF9F6] border border-[#E5E2DA] block hover:border-[#C9A961]/60 transition-colors duration-500"
              >
                <div className="aspect-[4/5] relative overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-[1400ms] ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1C] via-[#1C1C1C]/40 to-transparent" />
                </div>
                <div className="absolute inset-0 flex flex-col justify-end p-8 lg:p-10 text-[#FAF9F6]">
                  <div className="overline text-[#C9A961]">{p.category}</div>
                  <h3 className="font-serif-editorial text-3xl lg:text-4xl mt-3 leading-tight">{p.name}</h3>
                  <p className="mt-4 text-sm text-[#FAF9F6]/90 max-w-md leading-relaxed">
                    {p.tagline}
                  </p>
                  <div className="mt-6 pt-6 border-t border-[#FAF9F6]/20 flex items-end justify-between">
                    <div>
                      <div className="overline text-[#FAF9F6]/60">From</div>
                      <div className="text-2xl font-serif-editorial mt-1">{fmt(p.price)}</div>
                      <div className="text-[11px] text-[#FAF9F6]/70 mt-1 leading-tight">
                        <span className="text-[#E8D6A8] font-medium">{fmt(p.finance_monthly || Math.round(p.price / 60))}</span>
                        <span className="text-[#FAF9F6]/50"> / mo · est. 60 mo</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 overline text-[#C9A961] group-hover:gap-4 transition-all duration-500">
                      Explore
                      <ArrowUpRight
                        size={16}
                        className="group-hover:rotate-45 transition-transform duration-500"
                      />
                    </div>
                  </div>
                </div>
                {/* Gold accent bar */}
                <div className="absolute top-0 left-0 w-0 h-px bg-[#C9A961] group-hover:w-full transition-all duration-700 ease-out" />
              </Link>
              <ApproveButton
                price={p.price}
                model={p.name}
                qty={1}
                type="Hyperbaric Chamber"
                theme="light"
                className="mt-4"
              />
            </div>
          ))}
        </div>
      </section>

      <ClinicRenderStudio products={products} />

      {/* SIDE-BY-SIDE COMPARE */}
      <ChamberCompare />

      {/* THE SCIENCE — benefits grid */}
      <section
        ref={scienceRef}
        className={`bg-[#F5F3EE] border-y border-[#E5E2DA] py-24 lg:py-32 tt-reveal ${scienceRevealed ? "is-revealed" : ""}`}
      >
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <div className="overline text-[#C9A961]">Clinical Applications</div>
            <h2 className="font-serif-editorial text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight">
              A revenue line, <em className="italic text-[#C9A961]">not a machine.</em>
            </h2>
            <p className="mt-6 text-[#5C5C5C] text-sm md:text-base leading-relaxed">
              Every TheraTec hyperbaric chamber ships with the protocols, staff training, and consumables to book paid sessions in your first week open.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E2DA]">
            {SCIENCE_ITEMS.map((s) => (
              <div key={s.title} className="bg-[#FAF9F6] p-8 lg:p-10 hover:bg-white transition-colors duration-500 group">
                <s.icon size={28} strokeWidth={1.25} className="text-[#C9A961] group-hover:scale-110 transition-transform duration-500" />
                <h3 className="font-serif-editorial text-xl lg:text-2xl mt-8 leading-tight">{s.title}</h3>
                <p className="mt-4 text-sm text-[#5C5C5C] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <CertificationsStrip />

      {/* WHAT'S INCLUDED */}
      <section className="relative overflow-hidden max-w-[1200px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <SceneBackdrop variant="cream" label="RENEW" art="chamber" />
        <div className="relative z-10 grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="overline text-[#C9A961]">Every Order Includes</div>
            <h2 className="font-serif-editorial text-4xl lg:text-5xl mt-4 leading-tight">
              You buy equipment.<br />
              <em className="italic text-[#C9A961]">You receive a service line.</em>
            </h2>
          </div>
          <ul className="space-y-5 text-base text-[#1C1C1C]">
            {[
              "Coordinated white-glove crating and freight",
              "Certified on-site installation by our crew",
              "Full staff certification and safety training",
              "Launch-week protocol library — cash-pay session menus",
              "2-year parts & labor warranty",
              "Dedicated concierge from application through install",
            ].map((b) => (
              <li key={b} className="flex items-start gap-4 pb-5 border-b border-[#E5E2DA] last:border-b-0">
                <CheckCircle2 size={20} strokeWidth={1.5} className="text-[#C9A961] mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#F5F3EE] border-t border-[#E5E2DA] py-24 lg:py-28">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12 text-center">
          <div className="overline text-[#C9A961]">Concierge Consultation</div>
          <h2 className="font-serif-editorial text-4xl md:text-5xl lg:text-6xl mt-6 leading-tight">
            Ready to install <em className="italic text-[#C9A961]">the oxygen suite?</em>
          </h2>
          <p className="mt-8 text-base text-[#5C5C5C] max-w-2xl mx-auto leading-relaxed">
            Speak with a med-spa capital advisor about pressure, protocols, financing terms, and installation timeline — no obligation.
          </p>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <Link
              to="/#financing"
              className="inline-flex items-center gap-3 bg-[#1C1C1C] text-[#FAF9F6] px-10 py-5 font-medium tracking-wide hover:bg-[#C9A961] hover:text-[#1C1C1C] transition-colors duration-300"
              data-testid="hyperbaric-financing-cta"
            >
              Explore financing
              <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/integration"
              className="inline-flex items-center gap-3 border border-[#1C1C1C] px-10 py-5 font-medium tracking-wide hover:bg-[#1C1C1C] hover:text-[#FAF9F6] transition-colors duration-300"
            >
              Integration process
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Metric({ value, label }) {
  return (
    <div className="border-t border-[#E5E2DA] pt-6">
      <div className="font-serif-editorial text-3xl lg:text-4xl text-[#C9A961]">{value}</div>
      <div className="overline mt-2 text-[#5C5C5C]">{label}</div>
    </div>
  );
}

const SCIENCE_ITEMS = [
  {
    icon: Zap,
    title: "Recovery Acceleration",
    body: "Enhances ATP production in mitochondria — clients report visibly faster post-procedure healing and reduced downtime after aesthetic treatments.",
  },
  {
    icon: HeartPulse,
    title: "Cardiovascular Support",
    body: "Elevated tissue oxygenation improves circulation and endothelial function — a foundational longevity protocol layered with IV therapy.",
  },
  {
    icon: Brain,
    title: "Cognitive Clarity",
    body: "Increased cerebral oxygen perfusion is linked to improved focus, mental clarity, and reduced fatigue — a signature offering for executive clients.",
  },
  {
    icon: Sparkles,
    title: "Aesthetic Synergy",
    body: "Stimulates collagen synthesis and stem cell mobilization — the ideal pre/post protocol for laser resurfacing, PRP, and injectable programs.",
  },
  {
    icon: Wind,
    title: "Athletic Optimization",
    body: "Reduces lactic acid, accelerates muscle repair, and improves VO2 max — a premium recovery add-on for high-performance clientele.",
  },
  {
    icon: CheckCircle2,
    title: "Immune Modulation",
    body: "Activates the body's natural anti-inflammatory pathways — supports chronic condition management and general wellness programs.",
  },
];
